/* ================================================================
   HERO VIDEO PICKER — extracted, redesign era
   ----------------------------------------------------------------
   Carried forward verbatim from the pre-redesign homepage
   (templates/index.liquid, "CHECKPOINT 28 — HOME PAGE HERO VIDEO"),
   where it lived inline. Extracted into its own file during
   Redesign Checkpoint 1 (Foundation) so it survives the index.liquid
   rewrite — this logic is exactly the kind of thing the redesign
   keeps as engineering even though the page around it is being
   fully rebuilt (see LFJ_MASTER_REDESIGN_BUILD_DOCUMENT.md, Part B2).

   PURPOSE: picks one random eligible video from window.LF_CONFIG.videos
   each page load and plays it as a background/hero clip — with real
   failure recovery. A YouTube pick uses the real IFrame Player API
   (not a bare <iframe src>) specifically because a bare iframe can go
   silently black with zero JS error if the video is private, deleted,
   or has embedding disabled — the real API reports that (onError) so
   this can automatically retry a different eligible video instead of
   sitting on a dead black box. Local (Shopify-hosted) clips get the
   equivalent via an 'error' listener plus a 5s stall timeout.

   NOT YET RE-WIRED TO ANY PAGE. This file is intentionally inert
   until whichever future checkpoint rebuilds the LFJ room (the free-
   roam page where Jaydo wants this exact mechanic — see the identity
   doc: "the TV visibly cutting on and off... same mechanic, just put
   the LFJ logo"). At that point:
     1. Rebuild the surrounding markup/CSS for the new pixel aesthetic
        (the #homeBgVideoWrap / #homeBgVideo / #homeBgVideoYT ids
        below are just what this script expects to find — the visual
        treatment around those ids is entirely new).
     2. Re-include this script tag on that page, after lf-playlist.js
        (or whatever now sets window.LF_CONFIG.videos) has resolved
        window.LF_VIDEOS_READY.
     3. Confirm window.LF_CONFIG.heroVideo (whitelist/blacklist/
        priority/allowYouTube) is still bridged from the "Hero Video
        Rules (Advanced)" settings group — untouched by this
        checkpoint, still present in config/settings_schema.json.

   Depends on, at the page that re-includes it:
     - window.LF_CONFIG.videos        (array, from lf-playlist.js or
                                        successor)
     - window.LF_CONFIG.heroVideo     (object: whitelist, blacklist,
                                        priority, allowYouTube)
     - window.LF_VIDEOS_READY         (a Promise, if the video config
                                        loads asynchronously — falls
                                        back to Promise.resolve() if
                                        absent)
     - DOM: #homeBgVideoWrap, #homeBgVideo (a <video> element),
            #homeBgVideoYT (an empty container for the YT player)
================================================================ */

(function () {
  if (window.__lfHomeBgVideoInit) return;
  window.__lfHomeBgVideoInit = true;

  var SESSION_KEY = 'lfHeroPlayed';
  var tried = {};       // keys already attempted this pageview
  var ytPlayer = null;  // current YT.Player instance, if any
  var stallTimer = null;
  var ytApiPromise = null;

  /* Checkpoint 66 (final) — a YouTube video's videoUrl is a youtu.be link
     (the Discord bot writes both `id` and `videoUrl` for YouTube). This
     picker used to treat ANY videoUrl as a hosted file and hand the YouTube
     page to a native <video> element, which can't play it — the error handler
     then marked it "tried" and moved on, so YouTube videos never played in
     the hero at all (and the allowYouTube=false filter wrongly counted them as
     hosted files). Tell the two apart. */
  function isYouTubeUrl(u) { return /(?:youtu\.be\/|youtube\.com\/)/i.test(u || ''); }
  function isHosted(v) { return !!(v && v.videoUrl && !isYouTubeUrl(v.videoUrl)); }
  function ytIdFor(v) {
    if (!v) return '';
    if (v.id) return v.id;
    var m = String(v.videoUrl || '').match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{11})/);
    return m ? m[1] : '';
  }

  function eligiblePool(all, hero, excludeTried) {
    var pool = all.filter(function (v) { return v && (isHosted(v) || ytIdFor(v)); });
    /* Ad blockers commonly block youtube.com embeds outright — this
       is a blunter, more reliable fix than trying to guess/blacklist
       individual video IDs, since it has nothing to do with any one
       video and everything to do with the domain. */
    if (hero && hero.allowYouTube === false) {
      pool = pool.filter(function (v) { return isHosted(v); });
    }
    var whitelist = (hero && hero.whitelist) || [];
    var blacklist = (hero && hero.blacklist) || [];
    if (whitelist.length) {
      pool = pool.filter(function (v) { return whitelist.indexOf(v.key) !== -1; });
    } else if (blacklist.length) {
      pool = pool.filter(function (v) { return blacklist.indexOf(v.key) === -1; });
    }
    if (excludeTried) pool = pool.filter(function (v) { return !tried[v.key]; });
    return pool;
  }

  function loadYTApi() {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise(function (resolve) {
      if (window.YT && window.YT.Player) { resolve(); return; }
      var prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prevReady === 'function') prevReady();
        resolve();
      };
      var tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    });
    return ytApiPromise;
  }

  function clearStallTimer() {
    if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
  }

  function retry(ctx, failedKey) {
    if (failedKey) {
      tried[failedKey] = true;
      if (window.console && console.warn) {
        console.warn('[LF Hero Video] "' + failedKey + '" failed to load/play — trying a different video.');
      }
    }
    clearStallTimer();
    if (ytPlayer) { try { ytPlayer.destroy(); } catch (e) {} ytPlayer = null; }
    attempt(ctx);
  }

  function playLocal(ctx, pick) {
    var el = ctx.el, ytBox = ctx.ytBox;
    ytBox.style.display = 'none';
    ytBox.innerHTML = '';
    el.style.display = '';
    /* Explicit JS properties, not just the HTML attributes on the
       <video> tag — several browsers only reliably honor muted
       autoplay when `muted`/`playsInline` are set as properties,
       which matters more here because .src is being assigned
       programmatically (below) rather than present in the original
       markup. This is the single most common reason a "muted
       autoplay" video silently never actually starts. */
    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;
    el.setAttribute('webkit-playsinline', ''); /* older iOS Safari */
    el.src = pick.videoUrl;

    el.addEventListener('error', function onErr() {
      el.removeEventListener('error', onErr);
      retry(ctx, pick.key);
    }, { once: true });

    clearStallTimer();
    stallTimer = setTimeout(function () {
      if (el.readyState < 2) retry(ctx, pick.key);
    }, 5000);

    el.addEventListener('playing', clearStallTimer, { once: true });

    function tryPlay() {
      var p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* The first play() call can be rejected simply because the
             browser wasn't ready yet the instant .src was assigned
             (a benign AbortError, not a real autoplay block, since
             this video is muted) — one retry once real data has
             actually buffered fixes the vast majority of these
             instead of leaving the hero silently blank. */
          el.addEventListener('canplay', function onCanPlay() {
            el.removeEventListener('canplay', onCanPlay);
            el.play().catch(function () { clearStallTimer(); });
          }, { once: true });
        });
      }
    }
    tryPlay();
  }

  function playYouTube(ctx, pick) {
    var el = ctx.el, ytBox = ctx.ytBox;
    el.pause();
    el.removeAttribute('src');
    el.style.display = 'none';
    ytBox.style.display = '';
    ytBox.innerHTML = '<div id="lfHeroYTMount"></div>';

    loadYTApi().then(function () {
      if (ctx.token !== currentToken) return;
      ytPlayer = new YT.Player('lfHeroYTMount', {
        videoId: pick.id,
        playerVars: {
          autoplay: 1, mute: 1, controls: 0, loop: 1, playlist: pick.id,
          modestbranding: 1, rel: 0, showinfo: 0, iv_load_policy: 3,
          disablekb: 1, playsinline: 1, fs: 0
        },
        events: {
          onReady: function (e) { e.target.mute(); e.target.playVideo(); },
          onError: function () { retry(ctx, pick.key); }
        }
      });
    });
  }

  var currentToken = 0;

  function attempt(ctx) {
    currentToken += 1;
    ctx.token = currentToken;

    var pool = eligiblePool(ctx.all, ctx.hero, true);
    var pick = null;

    if (ctx.forcedPriority && !tried[ctx.forcedPriority]) {
      pick = ctx.all.filter(function (v) {
        if (!v || v.key !== ctx.forcedPriority) return false;
        if (ctx.hero && ctx.hero.allowYouTube === false) return isHosted(v);
        return !!(isHosted(v) || ytIdFor(v));
      })[0] || null;
      ctx.forcedPriority = null;
    }
    if (!pick) {
      if (pool.length === 0) { ctx.wrap.style.display = 'none'; return; }
      pick = pool[Math.floor(Math.random() * pool.length)];
    }

    if (isHosted(pick)) playLocal(ctx, pick);
    else if (ytIdFor(pick)) { pick.id = ytIdFor(pick); playYouTube(ctx, pick); }
    else retry(ctx, pick.key);
  }

  var lastCtx = null; // most recent {wrap, el, ytBox, all, hero} — reused by next()

  function pickAndPlay() {
    (window.LF_VIDEOS_READY || Promise.resolve()).then(function () { _runPick(); });
  }
  function _runPick() {
    var wrap = document.getElementById('homeBgVideoWrap');
    var el = document.getElementById('homeBgVideo');
    var ytBox = document.getElementById('homeBgVideoYT');
    if (!wrap || !el || !ytBox) return;

    var cfg = window.LF_CONFIG || {};
    var all = Array.isArray(cfg.videos) ? cfg.videos : [];
    var hero = cfg.heroVideo || {};

    var isFirstLoad = false;
    try { isFirstLoad = !sessionStorage.getItem(SESSION_KEY); } catch (e) { isFirstLoad = true; }
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* ignore */ }

    lastCtx = { wrap: wrap, el: el, ytBox: ytBox, all: all, hero: hero };

    attempt({
      wrap: wrap, el: el, ytBox: ytBox, all: all, hero: hero,
      forcedPriority: (isFirstLoad && hero.priority) ? hero.priority : null
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pickAndPlay);
  } else {
    pickAndPlay();
  }

  /* ── Manual "channel change" API ──────────────────────────────
     Added for the Menu-screen TV (Design Checkpoint 2, B13): a click
     on the TV should advance to a fresh random eligible video, same
     pool/whitelist rules as the automatic pick, but WITHOUT the
     first-load sessionStorage/forcedPriority logic (that's a once-
     per-session concern, not relevant mid-session on a manual click).
     Once `tried` has been exhausted the pool naturally recycles
     because eligiblePool() only filters by `tried` when asked to
     (excludeTried=true) — attempt() always requests that, so a full
     pass will eventually hide the wrap. To keep "next" always able to
     produce a video for as long as any are configured, a manual next()
     resets `tried` before picking so clicking never dead-ends. */
  window.LFJ_HeroPicker = {
    next: function () {
      if (!lastCtx) { _runPick(); return; }
      tried = {};
      attempt(lastCtx);
    }
  };
})();
